import React, { useState, useContext, useEffect } from "react";

import { useForm, Controller } from "react-hook-form";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import toast from "react-hot-toast";
import { accountTypeOptions, accountStatusOptions } from "utils/data";

const CreateUserModal = ({ visible, handleClose }) => {
  const account = useContext(AccountContext);

  const { register, handleSubmit, trigger, setValue, reset, control } = useForm();

  useEffect(() => {
    const accountStatus = accountStatusOptions.find((x) => x.value == "init");
    const accountType = accountTypeOptions.find((x) => x.value == "ma");
    reset({
      account_status: accountStatus,
      account_type: accountType,
    });
  }, []);

  const handleSave = async (data) => {
    if (!data["account_type"]) {
      return toast.error("Please select user account type");
    }

    try {
      const values = {
        email: data["email"],
        account_type: data["account_type"].value,
        account_status: data["account_status"].value,
        first_name: data["first_name"],
        last_name: data["last_name"],
        password: data["password"],
        role: "member",
        stores: [
          {
            name: data["name"],
          },
        ],
      };

      await account.handleRegister(values);

      handleClose();
      toast.success("Create user successfully!");
    } catch (err) {
      const message = err?.response?.data?.message || "Could not add user";
      toast.error(message);
    }
  };

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth={true}
    >
      <DialogTitle id="alert-dialog-title">Create User</DialogTitle>

      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          <form onSubmit={handleSubmit(handleSave)}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={12}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      required
                      textTransform="capitalize"
                    >
                      Business name
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput
                    placeholder="Business Name"
                    required
                    {...register("name", { required: true })}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      required
                      textTransform="capitalize"
                    >
                      Account Email
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput
                    placeholder="Email"
                    type="email"
                    {...register("email", { required: true })}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      required
                      textTransform="capitalize"
                    >
                      Password
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput
                    placeholder="Password"
                    type="password"
                    {...register("password", { required: true })}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      First Name
                    </SuiTypography>
                  </SuiBox>

                  <SuiInput placeholder="First Name" {...register("first_name")} />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      textTransform="capitalize"
                    >
                      Last Name
                    </SuiTypography>
                  </SuiBox>
                  <SuiInput placeholder="Last Name" {...register("last_name")} />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      required
                      textTransform="capitalize"
                    >
                      Account Type
                    </SuiTypography>
                  </SuiBox>
                  <Controller
                    rules={{ required: true }}
                    name="account_type"
                    control={control}
                    render={({ field }) => <SuiSelect {...field} options={accountTypeOptions} />}
                  />
                </SuiBox>
              </Grid>
              <Grid item xs={12} sm={6}>
                <SuiBox
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-end"
                  height="100%"
                >
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography
                      component="label"
                      variant="caption"
                      fontWeight="bold"
                      required
                      textTransform="capitalize"
                    >
                      Status
                    </SuiTypography>
                  </SuiBox>
                  <Controller
                    rules={{ required: true }}
                    name="account_status"
                    control={control}
                    render={({ field }) => <SuiSelect {...field} options={accountStatusOptions} />}
                  />
                </SuiBox>
              </Grid>
            </Grid>
            <SuiBox
              pt={3}
              px={3}
              display="flex"
              justifyContent="end"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton
                    variant="gradient"
                    size="medium"
                    onClick={handleClose}
                    className="mr-2"
                    color="light"
                  >
                    Cancel
                  </SuiButton>
                </SuiBox>
              </SuiBox>
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                    Create
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </form>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserModal;
